import { useEffect, useState } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Drawer, { FormField, getSaveLabel } from "@/components/Drawer";
import useBranchTypes from "@/hooks/useBranchTypes";

const EMPTY_BRANCH = {
  name: "",
  code: "",
  branch_type_id: "general",
  address: "",
  manager: "",
  manager_user_id: "",
  gstin: "",
  phone: "",
  email: "",
  trade_name: "",
  legal_name: "",
  pan: "",
  active: true,
  opening_cash: 0,
  opening_cash_as_of: "",
};

function branchToForm(b) {
  return {
    name: b.name || "",
    code: b.code || "",
    branch_type_id: b.branch_type_id || "general",
    address: b.address || "",
    manager: b.manager || "",
    manager_user_id: b.manager_user_id || "",
    gstin: b.gstin || "",
    phone: b.phone || "",
    email: b.email || "",
    trade_name: b.trade_name || "",
    legal_name: b.legal_name || "",
    pan: b.pan || "",
    active: b.active !== false,
    opening_cash: Number(b.opening_cash || 0),
    opening_cash_as_of: b.opening_cash_as_of || "",
  };
}

export default function BranchFormDrawer({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_BRANCH);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const { types: branchTypes } = useBranchTypes({ activeOnly: true });

  useEffect(() => {
    if (!open) return;
    setForm(editing ? branchToForm(editing) : EMPTY_BRANCH);
    api.get("/auth/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, [open, editing]);

  const confirm = useConfirm();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Save branch?", body: "Branch master will be updated. Confirm before saving." }))) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: (form.code || "").toUpperCase(),
        manager_user_id: form.manager_user_id || null,
        opening_cash: Number(form.opening_cash || 0),
        opening_cash_as_of: form.opening_cash_as_of || null,
      };
      if (editing) {
        await api.put(`/branches/${editing.id}`, payload);
        toast.success("Branch updated");
      } else {
        await api.post("/branches", payload);
        toast.success("Branch added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={`${editing ? "Edit" : "New"} Branch`}
      title={editing ? editing.name : "Add a branch"}
      testIdClose="close-branch-form"
    >
      <form onSubmit={handleSubmit} data-testid="branch-form" className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Branch Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="branch-name" required />
          <FormField label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} testId="branch-code" required mono />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Branch Type
          </label>
          <select
            data-testid="branch-type-select"
            value={form.branch_type_id}
            onChange={(e) => setForm({ ...form, branch_type_id: e.target.value })}
            required
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            {branchTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.code})
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-ink-subtle">
            Branches of the same type share a package catalog. Change with care.
            Set a branch type with code <code className="font-mono">SERVICE</code> to
            classify a branch as a workshop; all other codes are sales branches.
          </p>
        </div>
        <FormField label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="branch-address" />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Manager" value={form.manager} onChange={(v) => setForm({ ...form, manager: v })} testId="branch-manager" />
          <FormField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="branch-phone" />
          <FormField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="branch-email" />
          <FormField label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v.toUpperCase() })} testId="branch-gstin" mono />
        </div>
        <div className="border border-canvas-line p-3 space-y-3" data-testid="branch-letterhead">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
            Quote letterhead · address, phone, email &amp; GSTIN above are also printed · blank = head office
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Trade name (heading)" value={form.trade_name} onChange={(v) => setForm({ ...form, trade_name: v })} testId="branch-trade-name" placeholder="AMARAVATI TVS" />
            <FormField label="PAN" value={form.pan} onChange={(v) => setForm({ ...form, pan: v.toUpperCase() })} testId="branch-pan" mono placeholder="AAYCA9974G" />
          </div>
          <FormField label="Company legal name" value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} testId="branch-legal-name" placeholder="AMARAVATI AUTOMOTIVES PVT LTD" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            data-testid="branch-active"
          />
          Active
        </label>

        <div className="border-t border-canvas-line pt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-2">
            Cash Drawer — Day 0 Seed
          </div>
          <p className="text-[11px] text-ink-subtle mb-3">
            Cash physically present in this branch&apos;s till on the &ldquo;as of&rdquo; date. Used as the
            starting balance for the Cash Position report. Set once when the system goes live
            for the branch (or after a manual reconciliation).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="Opening cash (₹)"
              type="number"
              value={form.opening_cash}
              onChange={(v) => setForm({ ...form, opening_cash: v })}
              testId="branch-opening-cash"
              mono
            />
            <FormField
              label="As of date"
              type="date"
              value={form.opening_cash_as_of}
              onChange={(v) => setForm({ ...form, opening_cash_as_of: v })}
              testId="branch-opening-cash-as-of"
            />
          </div>
        </div>

        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Approving Manager
          </label>
          <select
            data-testid="branch-manager-user"
            value={form.manager_user_id}
            onChange={(e) => setForm({ ...form, manager_user_id: e.target.value })}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="">— Admin approval only —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email}) · {u.role}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-ink-subtle">
            This user can approve / reject incoming pending transfers to this branch. Admins can always approve.
          </p>
        </div>

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-branch-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, editing, "Saving…", "Save changes", "Add branch")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
